import { useEffect } from "react";
import { tinykeys } from "tinykeys";
import { useCockpit } from "./state";
import type { Project } from "../types/database";

// ----------------------------------------------------------------------------
// Centralized keyboard map. Single tinykeys binding registered on <body>.
// j/k movements need access to the live project list, so callers pass it in.
// ----------------------------------------------------------------------------

interface KeyboardArgs {
  projects: Project[];
  onNewTask: () => void;
  onFocusEditor: () => void;
}

export function useCockpitKeyboard({ projects, onNewTask, onFocusEditor }: KeyboardArgs) {
  const setPaletteOpen = useCockpit((s) => s.setPaletteOpen);
  const setHelpOpen = useCockpit((s) => s.setHelpOpen);
  const closeDrawer = useCockpit((s) => s.closeDrawer);
  const setSelected = useCockpit((s) => s.setSelectedProjectId);
  const drawer = useCockpit.getState().drawer;

  useEffect(() => {
    const move = (delta: number) => {
      const sel = useCockpit.getState().selectedProjectId;
      const ids = projects.map((p) => p.id);
      if (!ids.length) return;
      const idx = sel ? ids.indexOf(sel) : -1;
      const next = ids[(idx + delta + ids.length) % ids.length];
      setSelected(next);
    };
    const goEmbed = (slug: string) => {
      const match = projects.find(
        (p) =>
          p.id.toLowerCase() === slug ||
          p.name.toLowerCase().includes(slug),
      );
      if (match) setSelected(match.id);
    };

    const unsubscribe = tinykeys(window, {
      "$mod+KeyK": (e) => {
        e.preventDefault();
        setPaletteOpen(true);
      },
      "$mod+KeyP": (e) => {
        e.preventDefault();
        setPaletteOpen(true);
      },
      "$mod+KeyN": (e) => {
        e.preventDefault();
        onNewTask();
      },
      "$mod+KeyE": (e) => {
        e.preventDefault();
        onFocusEditor();
      },
      "Shift+Slash": (e) => {
        // ? — ignore when typing in inputs.
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        e.preventDefault();
        setHelpOpen(true);
      },
      Escape: () => {
        if (useCockpit.getState().paletteOpen) {
          setPaletteOpen(false);
          return;
        }
        if (useCockpit.getState().helpOpen) {
          setHelpOpen(false);
          return;
        }
        closeDrawer();
      },
      KeyJ: (e) => {
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        move(1);
      },
      KeyK: (e) => {
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        move(-1);
      },
      "KeyG KeyR": () => goEmbed("row1"),
      "KeyG KeyT": () => goEmbed("tagral"),
      "KeyG KeyS": () => goEmbed("services"),
    });

    return unsubscribe;
  }, [projects, setPaletteOpen, setHelpOpen, closeDrawer, setSelected, onNewTask, onFocusEditor, drawer]);
}
